import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Crop, BgRot, GoldButton, makeSparks, fitScene, inScene, useEntering } from './Shared';
import { BackIcon } from './Icons';

/* ------------------------------------------------------------------
   Pre-registration: claim the free Gems before launch, optionally with a
   launch reminder. Tapping the CTA calls onClaim(remind) (the API); on
   success the screen celebrates (sparks, "+5", claimed copy) and after
   1.9s hands back to the hub via onDone(remind, result), `result` being
   what onClaim resolved to (the Gems the backend reserved). Laid out on the
   375px stage; the parent sizes/zooms it to the viewport.
   ------------------------------------------------------------------ */

const SUCCESS_MS = 1900;
/* "+5" / gems / copy / reminder card: top:54 → card bottom 576 on the design;
   may rise to 48 (under the back arrow) and must clear the fine print
   (bottom:107 + 15 tall) by 12 */
const SCENE = { designTop: 54, height: 522, minTop: 48, bottom: 134 };

function copyFor(stage, gems, remind) {
  if (stage === 'success') {
    return {
      h: `${gems} Gems claimed`,
      p: "They're reserved in your festival wallet and unlock the moment The Golden Quest opens on 18 October.",
      fine: remind ? "We'll remind you at 12:00 AM on 18 Oct." : 'You can set a reminder from the festival page any time.',
    };
  }
  return {
    h: `${gems} Gems, on us`,
    p: `Register now to receive ${gems} free Gems that you can open on 18th October. Each one an instant prize in Bitcoin or Gold.`,
    fine: 'Gems unlock on 18 Oct. Pre-registration is free and takes one tap.',
  };
}

/* the entrance stagger (cluster → copy → reminder → fine print → CTA) runs
   this long after mount */
const ENTER_MS = 1300;

export default function PreReg({
  gems, remind: initialRemind = true, zoom = 1, stageHeight = 812,
  onBack, onClaim, onDone, onError,
}) {
  const [remind, setRemind] = useState(initialRemind);
  const [stage, setStage] = useState('idle'); // idle | claiming | success
  const [sparks, setSparks] = useState([]);
  const timers = useRef([]);
  const alive = useRef(true);
  const fit = useMemo(() => fitScene(stageHeight, SCENE), [stageHeight]);
  const entering = useEntering(ENTER_MS);

  useEffect(() => {
    const pending = timers.current;
    return () => { alive.current = false; pending.forEach(clearTimeout); };
  }, []);

  const claim = useCallback(() => {
    if (stage !== 'idle') return;
    setStage('claiming');
    Promise.resolve()
      .then(() => onClaim(remind))
      .then((result) => {
        if (!alive.current) return;
        setStage('success');
        /* burst from the gem cluster's centre (230 on the design) wherever the scene sits */
        setSparks(makeSparks(187, inScene(fit, 230, SCENE.designTop), 28));
        timers.current.push(setTimeout(() => setSparks([]), 1300));
        timers.current.push(setTimeout(() => onDone(remind, result), SUCCESS_MS));
      })
      .catch((err) => {
        if (!alive.current) return;
        setStage('idle');
        if (onError) onError(err);
      });
  }, [stage, remind, fit, onClaim, onDone, onError]);

  const copy = copyFor(stage, gems, remind);
  const label = stage === 'claiming' ? 'Adding your Gems…' : (stage === 'success' ? 'Taking you in…' : `Claim ${gems} Gems`);
  const busy = stage !== 'idle';

  return (
    <div className="gq-overlay">
      <div
        className={`gq-prereg${stage === 'success' ? ' gq-success' : ''}${entering ? ' gq-entering' : ''}`}
        style={{ zoom, height: stageHeight }}
      >
        <BgRot />
        <div className="gq-glow" style={{ top: 250 }} />
        <button type="button" className="gq-closeb" onClick={onBack} aria-label="Back" disabled={busy}>
          <BackIcon />
        </button>
        <div className="gq-sparks">
          {sparks.map((s) => <i key={s.id} className="gq-spark" style={s.style} />)}
        </div>

        <div className="gq-scene" style={fit.style}>
          <div className="gq-cluster">
            <Crop kind="gem" className="gq-gemA gq-rot" />
            <Crop kind="gem" className="gq-gemB gq-rot" />
            <Crop kind="gem" className="gq-gemC gq-rot" />
          </div>
          <div className="gq-count" aria-hidden="true">+{gems}</div>

          <div className="gq-copy" aria-live="polite">
            <div className="gq-h">{copy.h}</div>
            <div className="gq-p">{copy.p}</div>
          </div>

          <div className="gq-meta">
            <div>
              <div className="gq-k">Remind me when it starts</div>
              <div className="gq-v">18 Oct, 12:00 AM</div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={remind}
              aria-label="Remind me when it starts"
              className={`gq-sw${remind ? '' : ' gq-off'}`}
              disabled={busy}
              onClick={() => setRemind((r) => !r)}
            />
          </div>
        </div>

        <div className="gq-fine">{copy.fine}</div>
        <GoldButton disabled={stage === 'success'} onClick={claim} aria-busy={busy}>{label}</GoldButton>
      </div>
    </div>
  );
}
